/**
 * Order Items Table Enriched Component
 *
 * Read-only table displaying order line items with received/remaining columns.
 * Used in the order detail view for sent/partially received orders.
 * @see US-9.6 - Action "Recevoir" sur Commande
 */

import { Check, AlertCircle, Package } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatCentimes } from '@/lib/format';
import { useCurrency } from '@/hooks';
import type { PurchaseOrderItem } from '@bstock/shared';

interface OrderItemsTableEnrichedProps {
  /** Order items with quantityReceived info */
  items: PurchaseOrderItem[];
  /** CSS class name */
  className?: string;
  /** Show financial data (prices, totals) */
  showFinancials?: boolean;
}

export function OrderItemsTableEnriched({
  items,
  className,
  showFinancials = false,
}: OrderItemsTableEnrichedProps) {
  const currency = useCurrency();

  // Calculate total
  const totalExclTax = items.reduce((sum, item) => sum + item.totalPriceExclTax, 0);

  // Calculate aggregates for progress
  const totalOrdered = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalReceived = items.reduce((sum, item) => sum + item.quantityReceived, 0);

  return (
    <div className={cn('space-y-4', className)}>
      {/* Progress summary */}
      <div className="flex items-center justify-between px-4 py-2 bg-neutral-50 rounded-lg">
        <span className="text-sm text-neutral-600">
          Reception : {totalReceived}/{totalOrdered} articles
        </span>
        <span className={cn(
          'text-sm font-medium',
          totalReceived >= totalOrdered ? 'text-success-600' : 'text-warning-600'
        )}>
          {totalOrdered > 0 ? Math.round((totalReceived / totalOrdered) * 100) : 0}%
        </span>
      </div>

      {/* Table */}
      <div className="border border-neutral-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-medium text-neutral-700">
                Produit
              </th>
              <th className="text-left px-4 py-3 text-sm font-medium text-neutral-700 w-40">
                Conditionnement
              </th>
              <th className="text-center px-4 py-3 text-sm font-medium text-neutral-700 w-20">
                Qte
              </th>
              <th className="text-center px-4 py-3 text-sm font-medium text-neutral-700 w-24">
                Recu
              </th>
              <th className="text-center px-4 py-3 text-sm font-medium text-neutral-700 w-28">
                Reste
              </th>
              {showFinancials && (
                <>
                  <th className="text-right px-4 py-3 text-sm font-medium text-neutral-700 w-28">
                    PU HT
                  </th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-neutral-700 w-28">
                    Total
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={showFinancials ? 7 : 5} className="px-4 py-8 text-center">
                  <div className="flex flex-col items-center gap-2 text-neutral-400">
                    <Package className="w-8 h-8" />
                    <span className="text-sm">Aucun produit</span>
                  </div>
                </td>
              </tr>
            ) : (
              items.map((item) => {
                const isComplete = item.quantityReceived >= item.quantity;
                const remaining = item.quantity - item.quantityReceived;
                const hasPartialReceipt = item.quantityReceived > 0 && !isComplete;

                return (
                  <tr
                    key={item.id}
                    className={cn(
                      'border-b border-neutral-100 last:border-0',
                      isComplete && 'bg-success-50/30',
                      hasPartialReceipt && 'bg-warning-50/30'
                    )}
                  >
                    {/* Product name */}
                    <td className="px-4 py-3">
                      <div className="font-medium text-dark-500">{item.rawProductName}</div>
                    </td>

                    {/* Packaging */}
                    <td className="px-4 py-3">
                      <span className="text-sm text-neutral-600">{item.packagingName}</span>
                    </td>

                    {/* Ordered quantity */}
                    <td className="px-4 py-3 text-center text-sm">
                      {item.quantity}
                    </td>

                    {/* Received quantity */}
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <span className={cn(
                          'text-sm',
                          isComplete ? 'text-success-600 font-medium' : 'text-neutral-600'
                        )}>
                          {item.quantityReceived}/{item.quantity}
                        </span>
                        {isComplete && (
                          <Check className="w-4 h-4 text-success-500" />
                        )}
                      </div>
                    </td>

                    {/* Remaining quantity */}
                    <td className="px-4 py-3 text-center">
                      {isComplete ? (
                        <span className="text-sm text-neutral-400">-</span>
                      ) : (
                        <div className="flex items-center justify-center gap-1">
                          <AlertCircle className="w-3.5 h-3.5 text-warning-500" />
                          <span className="text-sm text-warning-600">
                            {remaining} manquant{remaining > 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                    </td>

                    {/* Unit price */}
                    {showFinancials && (
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm text-neutral-600">
                          {formatCentimes(item.unitPriceExclTax, currency)}
                        </span>
                      </td>
                    )}

                    {/* Line total */}
                    {showFinancials && (
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm font-medium text-dark-500">
                          {formatCentimes(item.totalPriceExclTax, currency)}
                        </span>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
          {/* Footer with total */}
          {showFinancials && (
            <tfoot className="bg-neutral-50 border-t border-neutral-200">
              <tr>
                <td colSpan={6} className="px-4 py-3 text-right">
                  <span className="text-sm font-medium text-neutral-700">Total HT</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-lg font-semibold text-dark-500">
                    {formatCentimes(totalExclTax, currency)}
                  </span>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
